import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { firstValueFrom } from 'rxjs';

/**
 * Interface que mapea la respuesta del backend para lab_results
 * Nota: El backend devuelve campos en español (nombre, fecha, tipo, interpretacion)
 */
/**
 * Información de un archivo individual asociado a un lab_result
 */
export interface ILabResultFile {
  id: number | null;
  url: string;
  nombre: string;
  tipo: string; // mime_type
  tamanio: number | null;
  esImagen: boolean;
}

export interface ILabResultResponse {
  id: number;
  appointment_id: number | null;
  patient_id: number;
  medical_record_id?: number; // ID de la nota médica asociada
  // Campos en español como los devuelve el backend
  tipo: 'interno' | 'laboratorio' | 'gabinete';
  nombre: string;
  fecha: string;
  interpretacion?: string;
  pdf_url?: string;
  file_url?: string; // URL del primer archivo (compatibilidad)
  file_urls?: string[]; // URLs de todos los archivos
  files?: ILabResultFile[]; // Info completa de todos los archivos
  // Campos legacy para compatibilidad (alias)
  test_type?: 'interno' | 'laboratorio' | 'gabinete';
  test_name?: string;
  test_date?: string;
  interpretation?: string;
  file_id?: number;
  analyzed_automatically?: boolean;
  analysis_metadata?: {
    fechaAnalisis: string;
    totalParametros: number;
    parametrosFueraRango: number;
    confianzaPromedio: number;
  };
  created_at: string;
  updated_at: string;
  file?: {
    id: number;
    url: string;
    filename: string;
  };
  patientFile?: {
    id: number;
    file_path: string;
    file_name: string;
    file_size: number;
    mime_type: string;
  };
  patientFiles?: Array<{
    id: number;
    file_path: string;
    file_name: string;
    file_size: number;
    mime_type: string;
  }>;
}

/**
 * Datos para crear un nuevo lab result
 */
export interface ICreateLabResultData {
  test_type?: 'interno' | 'laboratorio' | 'gabinete';
  test_name: string;
  test_date: string;
  interpretation?: string;
  analyzed_automatically?: boolean;
  analysis_metadata?: {
    fechaAnalisis: string;
    totalParametros: number;
    parametrosFueraRango: number;
    confianzaPromedio: number;
  };
}

/**
 * Datos para actualizar un lab result existente
 * Nota: El backend usa campos en espanol (interpretacion, nombre, tipo, fecha)
 */
export interface IUpdateLabResultData {
  tipo?: 'interno' | 'laboratorio' | 'gabinete';
  nombre?: string;
  fecha?: string;
  interpretacion?: string;
  analyzed_automatically?: boolean;
  analysis_metadata?: {
    fechaAnalisis: string;
    totalParametros: number;
    parametrosFueraRango: number;
    confianzaPromedio: number;
  };
  medical_record_id?: number | string;
}

/**
 * Servicio para gestión de resultados de laboratorio (paraclínicos)
 *
 * Este servicio se conecta al endpoint `/api/lab-results` del backend
 * y maneja la creación, lectura, actualización y eliminación de paraclínicos
 * con archivos PDF almacenados en la tabla `patient_files`.
 */
@Injectable({
  providedIn: 'root',
})
export class LabResultsService {
  private readonly endpoint = 'lab-results';

  constructor(private apiService: ApiService) {}

  /**
   * Crea un nuevo resultado de laboratorio con uno o múltiples archivos
   *
   * @param appointmentId ID de la cita asociada (puede ser null si no hay cita activa)
   * @param patientId ID del paciente
   * @param files Archivo(s) del paraclínico (PDF o imágenes)
   * @param data Datos del paraclínico (nombre, fecha, interpretación, etc.)
   * @returns Promise con el lab result creado
   */
  async create(
    appointmentId: number | string | null,
    patientId: number | string,
    files: File | File[],
    data: ICreateLabResultData
  ): Promise<ILabResultResponse> {
    const filesArray = Array.isArray(files) ? files : [files];

    console.log('[LabResultsService] create - Inicio', {
      appointmentId,
      patientId,
      filesCount: filesArray.length,
      files: filesArray.map(f => ({ name: f.name, size: f.size, type: f.type })),
      data,
    });

    try {
      // Validar cada archivo
      const tiposPermitidos = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
      const maxSize = 10 * 1024 * 1024; // 10MB

      for (const file of filesArray) {
        if (!tiposPermitidos.includes(file.type)) {
          const error = `El archivo "${file.name}" debe ser PDF, PNG, JPG o JPEG`;
          console.error('[LabResultsService] Error de validación:', error);
          throw new Error(error);
        }

        if (file.size > maxSize) {
          const error = `El archivo "${file.name}" es muy grande. Máximo 10MB.`;
          console.error('[LabResultsService] Error de validación:', error);
          throw new Error(error);
        }
      }

      // Crear FormData para enviar archivos + datos JSON
      const formData = new FormData();

      // Enviar archivos usando files[] para múltiples o pdf para uno solo (compatibilidad)
      if (filesArray.length === 1) {
        formData.append('pdf', filesArray[0]); // Legacy: un solo archivo
      } else {
        filesArray.forEach((file) => {
          formData.append('files[]', file); // Nuevo: múltiples archivos
        });
      }

      // Solo agregar appointment_id si no es null
      if (appointmentId !== null) {
        formData.append('appointment_id', appointmentId.toString());
      }

      formData.append('patient_id', patientId.toString());
      formData.append('nombre', data.test_name); // Backend espera 'nombre', no 'test_name'
      formData.append('fecha', data.test_date); // Backend espera 'fecha', no 'test_date'

      if (data.test_type) {
        formData.append('tipo', data.test_type); // Backend espera 'tipo', no 'test_type'
      }

      if (data.interpretation) {
        formData.append('interpretacion', data.interpretation); // Backend espera 'interpretacion'
      }

      if (data.analyzed_automatically !== undefined) {
        formData.append('analyzed_automatically', data.analyzed_automatically ? '1' : '0');
      }

      if (data.analysis_metadata) {
        formData.append('analysis_metadata', JSON.stringify(data.analysis_metadata));
      }

      console.log('[LabResultsService] Enviando FormData al backend...');
      const startTime = Date.now();

      // Enviar la petición usando el método postFormData del ApiService
      const response = await firstValueFrom(
        this.apiService.postFormData<{ data: ILabResultResponse }>(
          this.endpoint,
          formData
        )
      );

      const uploadTime = Date.now() - startTime;
      console.log(`[LabResultsService] Lab result creado exitosamente en ${uploadTime}ms`, response.data);

      return response.data;
    } catch (error) {
      console.error('[LabResultsService] Error en create:', error);
      console.error('[LabResultsService] Stack trace:', (error as any)?.stack);
      throw error;
    }
  }

  /**
   * Obtiene todos los lab results de una cita específica
   *
   * @param appointmentId ID de la cita
   * @returns Promise con array de lab results
   */
  async getByAppointment(appointmentId: number | string): Promise<ILabResultResponse[]> {
    console.log('[LabResultsService] getByAppointment - appointmentId:', appointmentId);

    try {
      const response = await firstValueFrom(
        this.apiService.get<{ data: ILabResultResponse[] }>(
          `${this.endpoint}?appointment_id=${appointmentId}`
        )
      );

      console.log('[LabResultsService] Lab results obtenidos:', response.data.length);
      return response.data;
    } catch (error) {
      console.error('[LabResultsService] Error en getByAppointment:', error);
      throw error;
    }
  }

  /**
   * Obtiene todos los lab results de un paciente específico
   *
   * @param patientId ID del paciente
   * @returns Promise con array de lab results
   */
  async getByPatient(patientId: number | string): Promise<ILabResultResponse[]> {
    console.log('[LabResultsService] getByPatient - patientId:', patientId);

    try {
      const response = await firstValueFrom(
        this.apiService.get<{ data: ILabResultResponse[] }>(
          `${this.endpoint}?patient_id=${patientId}`
        )
      );

      console.log('[LabResultsService] Lab results del paciente obtenidos:', response.data.length);
      return response.data;
    } catch (error: any) {
      console.error('[LabResultsService] Error en getByPatient:', error);

      // Si es 404, probablemente el endpoint no existe en el backend
      if (error?.status === 404) {
        console.error('[LabResultsService] ❌ ENDPOINT NO ENCONTRADO: GET /api/lab-results?patient_id=${patientId}');
        console.error('[LabResultsService] ❌ El backend necesita implementar este endpoint para cargar paraclínicos por paciente');
        throw new Error('Endpoint no implementado en el backend. Contacta al equipo de desarrollo.');
      }

      throw error;
    }
  }

  /**
   * Obtiene un lab result específico por ID
   *
   * @param id ID del lab result
   * @returns Promise con el lab result
   */
  async getById(id: number | string): Promise<ILabResultResponse> {
    console.log('[LabResultsService] getById - id:', id);

    try {
      const response = await firstValueFrom(
        this.apiService.get<{ data: ILabResultResponse }>(`${this.endpoint}/${id}`)
      );

      console.log('[LabResultsService] Lab result obtenido:', response.data);
      return response.data;
    } catch (error) {
      console.error('[LabResultsService] Error en getById:', error);
      throw error;
    }
  }

  /**
   * Actualiza un lab result existente
   *
   * @param id ID del lab result a actualizar
   * @param data Datos a actualizar (interpretación, nombre, fecha, etc.)
   * @returns Promise con el lab result actualizado
   */
  async update(
    id: number | string,
    data: IUpdateLabResultData
  ): Promise<ILabResultResponse> {
    console.log('[LabResultsService] update - id:', id, 'data:', data);

    try {
      const response = await firstValueFrom(
        this.apiService.put<{ data: ILabResultResponse }>(
          `${this.endpoint}/${id}`,
          data
        )
      );

      console.log('[LabResultsService] Lab result actualizado:', response.data);
      return response.data;
    } catch (error) {
      console.error('[LabResultsService] Error en update:', error);
      throw error;
    }
  }

  /**
   * Elimina un lab result
   *
   * @param id ID del lab result a eliminar
   * @returns Promise que se resuelve cuando se elimina
   */
  async delete(id: number | string): Promise<void> {
    console.log('[LabResultsService] delete - id:', id);

    try {
      await firstValueFrom(
        this.apiService.delete(`${this.endpoint}/${id}`)
      );

      console.log('[LabResultsService] Lab result eliminado exitosamente');
    } catch (error) {
      console.error('[LabResultsService] Error en delete:', error);
      throw error;
    }
  }

  /**
   * Actualiza solo la interpretación de un lab result
   * Método de conveniencia para ediciones rápidas
   *
   * @param id ID del lab result
   * @param interpretation Nueva interpretación
   * @returns Promise con el lab result actualizado
   */
  async updateInterpretation(
    id: number | string,
    interpretacion: string
  ): Promise<ILabResultResponse> {
    console.log('[LabResultsService] updateInterpretation - id:', id, 'interpretacion length:', interpretacion?.length);

    return this.update(id, { interpretacion });
  }

  /**
   * Obtiene todos los lab results de una nota médica específica
   *
   * @param medicalRecordId ID del medical record (nota médica)
   * @returns Promise con array de lab results
   */
  async getByMedicalRecord(medicalRecordId: number | string): Promise<ILabResultResponse[]> {
    console.log('[LabResultsService] getByMedicalRecord - medicalRecordId:', medicalRecordId);

    try {
      const response = await firstValueFrom(
        this.apiService.get<{ data: ILabResultResponse[] }>(
          `${this.endpoint}?medical_record_id=${medicalRecordId}`
        )
      );

      console.log('[LabResultsService] Lab results por medical record obtenidos:', response.data.length);
      return response.data;
    } catch (error: any) {
      console.error('[LabResultsService] Error en getByMedicalRecord:', error);

      if (error?.status === 404) {
        console.error('[LabResultsService] ❌ No se encontraron paraclínicos para esta nota médica');
        return [];
      }

      throw error;
    }
  }

  /**
   * Actualiza el medical_record_id de todos los lab results de una cita
   * Útil para asociar paraclínicos con una consulta médica después de crearla
   *
   * @param appointmentId ID de la cita
   * @param medicalRecordId ID del medical record (nota médica)
   * @returns Promise que se resuelve cuando se actualizan todos
   */
  async updateMedicalRecordId(
    appointmentId: number | string,
    medicalRecordId: number | string
  ): Promise<void> {
    console.log('[LabResultsService] updateMedicalRecordId - appointmentId:', appointmentId, 'medicalRecordId:', medicalRecordId);

    try {
      // Primero obtenemos todos los lab results de la cita
      const labResults = await this.getByAppointment(appointmentId);
      console.log('[LabResultsService] Lab results a actualizar:', labResults.length);

      // Actualizamos cada uno con el medical_record_id
      const updatePromises = labResults.map((labResult) => {
        console.log('[LabResultsService] Actualizando lab result ID:', labResult.id);
        return firstValueFrom(
          this.apiService.put<{ data: ILabResultResponse }>(
            `${this.endpoint}/${labResult.id}`,
            { medical_record_id: medicalRecordId }
          )
        );
      });

      await Promise.all(updatePromises);
      console.log('[LabResultsService] Todos los lab results actualizados con medical_record_id');
    } catch (error) {
      console.error('[LabResultsService] Error en updateMedicalRecordId:', error);
      throw error;
    }
  }

  /**
   * Actualiza los paraclínicos de un paciente que NO tienen medical_record_id
   * asignado, asociándolos con la nota médica especificada.
   *
   * Útil cuando los paraclínicos se guardaron antes de crear la nota médica.
   *
   * @param patientId ID del paciente
   * @param medicalRecordId ID de la nota médica
   * @returns Promise con el número de paraclínicos actualizados
   */
  async updateOrphanedByPatient(
    patientId: number | string,
    medicalRecordId: number | string
  ): Promise<number> {
    console.log('[LabResultsService] updateOrphanedByPatient - patientId:', patientId, 'medicalRecordId:', medicalRecordId);

    try {
      const labResults = await this.getByPatient(patientId);
      console.log('[LabResultsService] Total lab results del paciente:', labResults.length);

      const orphaned = labResults.filter((lr: any) => !lr.medical_record_id);
      console.log('[LabResultsService] Lab results sin medical_record_id:', orphaned.length);

      if (orphaned.length === 0) {
        console.log('[LabResultsService] No hay lab results huérfanos para actualizar');
        return 0;
      }

      const updatePromises = orphaned.map((labResult) => {
        console.log('[LabResultsService] Actualizando lab result ID:', labResult.id);
        return firstValueFrom(
          this.apiService.put<{ data: ILabResultResponse }>(
            `${this.endpoint}/${labResult.id}`,
            { medical_record_id: medicalRecordId }
          )
        );
      });

      await Promise.all(updatePromises);
      console.log('[LabResultsService] ✅', orphaned.length, 'lab results actualizados con medical_record_id');
      return orphaned.length;
    } catch (error) {
      console.error('[LabResultsService] Error en updateOrphanedByPatient:', error);
      throw error;
    }
  }

  /**
   * Copia un lab result a una nueva nota médica sin duplicar el archivo PDF
   *
   * Útil para cuando se cargan paraclínicos de una nota anterior y se quiere
   * asociarlos a una nueva nota sin duplicar archivos en storage.
   *
   * @param labResultId ID del lab result original a copiar
   * @param medicalRecordId ID de la nueva nota médica
   * @returns Promise con el nuevo lab result creado (o null si ya existía)
   */
  async copyToMedicalRecord(
    labResultId: number | string,
    medicalRecordId: number | string
  ): Promise<ILabResultResponse | null> {
    console.log('[LabResultsService] copyToMedicalRecord - labResultId:', labResultId, 'medicalRecordId:', medicalRecordId);

    try {
      const response = await firstValueFrom(
        this.apiService.post<{ data: ILabResultResponse | null; skipped?: boolean }>(
          `${this.endpoint}/${labResultId}/copy`,
          { medical_record_id: medicalRecordId }
        )
      );

      if (response.skipped) {
        console.log('[LabResultsService] Lab result ya existía en la nota destino, omitido');
        return null;
      }

      console.log('[LabResultsService] Lab result copiado exitosamente:', response.data);
      return response.data;
    } catch (error) {
      console.error('[LabResultsService] Error en copyToMedicalRecord:', error);
      throw error;
    }
  }

  /**
   * Copia múltiples lab results a una nueva nota médica
   *
   * @param labResultIds Array de IDs de lab results a copiar
   * @param medicalRecordId ID de la nueva nota médica
   * @returns Promise con array de resultados (algunos pueden ser null si ya existían)
   */
  async copyMultipleToMedicalRecord(
    labResultIds: (number | string)[],
    medicalRecordId: number | string
  ): Promise<(ILabResultResponse | null)[]> {
    console.log('[LabResultsService] copyMultipleToMedicalRecord - ids:', labResultIds.length, 'medicalRecordId:', medicalRecordId);

    const results: (ILabResultResponse | null)[] = [];

    for (const id of labResultIds) {
      try {
        const result = await this.copyToMedicalRecord(id, medicalRecordId);
        results.push(result);
      } catch (error) {
        console.error(`[LabResultsService] Error copiando lab result ${id}:`, error);
        results.push(null);
      }
    }

    const copied = results.filter(r => r !== null).length;
    console.log(`[LabResultsService] Copiados ${copied}/${labResultIds.length} lab results`);

    return results;
  }
}
